// ---------------------------------------------------------------------------
// src/config/themes.ts
// Fonte de verdade única para todos os temas da aplicação.
//
// Regras de negócio:
//  - Cozy Dark, Blush, Noir e default são gratuitos.
//  - Midnight, Matcha (isPremium) e Obsidian exigem assinatura — ver themeRequiresSubscription.
//  - FREE_THEME_IDS deriva dessa função — onboarding e validação de perfil.
// ---------------------------------------------------------------------------

export type ThemeStyleType = "standard" | "neon-glass";

export interface ThemeOption {
  id: string;
  label: string;
  description: string;
  sublabel: string;
  isPro: boolean;
  /** Tema exclusivo assinatura (paywall de UI); combina com isPro para acesso. */
  isPremium?: boolean;
  /**
   * Tema exclusivo de Founder (`subscriptions.isLifetime`). Deliberadamente
   * SEPARADO de `isPro`: assinatura ativa não destrava, e marcar `isPro: true`
   * o venderia no teaser de PRO do onboarding. Também é o que impede que ele
   * caia em `FREE_THEME_IDS` — a lista é derivada por exclusão, então um tema
   * novo que não declare nenhum gate nasce GRATUITO para todo mundo.
   */
  isFounder?: boolean;
  /**
   * Tema CONQUISTADO: o direito vem da badge nomeada aqui, e de nada mais.
   *
   * Diferente dos outros três gates, este NÃO tira o tema de
   * `FREE_THEME_IDS` — e a assimetria é o ponto. Aquela lista é o que o guard
   * de PRO consulta, então excluir o tema conquistado dali transformaria toda
   * conta grátis que ganhou a badge em recusada: o item que ela conquistou
   * viraria, na prática, exclusivo de assinante. É a mesma razão pela qual
   * tema comprado com moeda vive lá dentro (ver A4 em
   * `docs/systems/cosmetics-expansion.md`): quem barra não é a lista larga, é
   * o guard próprio — aqui `EARNED_THEME_IDS` + `themeBadgeRequirement`.
   */
  requiresBadge?: string;
  /**
   * Tema de EVENTO (Halloween, 07/10/2026): grátis e emprestado a todo mundo
   * só enquanto o evento de `themeEvents.ts` está no ar. Fora dele o
   * resolvedor devolve o tema de antes (`themeBeforeEvent`). É o único gate
   * em que a DATA decide o direito — ver o cabeçalho de `themeEvents.ts` para
   * o porquê de isso nunca valer para tema pago.
   *
   * Fica dentro de `FREE_THEME_IDS` pela mesma razão do tema conquistado: o
   * guard de PRO consulta aquela lista, e quem barra é o guard próprio.
   */
  event?: string;
  /** `neon-glass`: UI global com vidro (BottomNav, headers). Padrão: standard. */
  styleType?: ThemeStyleType;
  palette: {
    bg: string;
    surface: string;
    primary: string;
    secondary: string;
  };
}

export const THEMES: ThemeOption[] = [
  {
    // Redesign 07/2026: 'default' = Linho (id preservado; ex-default virou 'cozy')
    id: "default",
    label: "Linho",
    description: "Terracota quente sobre papel — o padrão editorial.",
    sublabel: "Gratuito",
    isPro: false,
    styleType: "standard",
    palette: {
      bg: "#f4f1ec",
      surface: "#ffffff",
      primary: "#bc4b2f",
      secondary: "#a86f1d",
    },
  },
  {
    id: "cozy",
    label: "Cozy Dark",
    description: "Quente, aconchegante e literário.",
    sublabel: "Gratuito",
    isPro: false,
    styleType: "standard",
    palette: {
      bg: "#101010",
      surface: "#1a1a1a",
      primary: "#f1dfc8",
      secondary: "#dfb682",
    },
  },
  {
    id: "midnight",
    label: "Midnight",
    description: "Sombrio, intenso e dramático.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "standard",
    palette: {
      bg: "#0a090e",
      surface: "#14121f",
      // primary elevado para rosa claro — contraste 7:1 sobre bg escuro
      primary: "#f2eaf0",
      // secondary: vermelho-rosa médio — legível e com personalidade
      secondary: "#bf3f5a",
    },
  },
  {
    id: "matcha",
    label: "Matcha Cottagecore",
    description: "Natural, sereno e encantador.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "standard",
    palette: {
      bg: "#F2EFE9",
      surface: "#E5E2D9",
      primary: "#6A7B62",
      secondary: "#9EB096",
    },
  },
  {
    id: "noir",
    label: "Noir",
    description: "Neutro escuro minimalista — mesma paleta do antigo tema escuro.",
    sublabel: "Gratuito",
    isPro: false,
    styleType: "standard",
    palette: {
      bg: "#111111",
      surface: "#1c1c1c",
      primary: "#e2e2e2",
      secondary: "#888888",
    },
  },
  {
    id: "cyberpunk",
    label: "Cyberpunk",
    description: "Fundo roxo profundo, neon ciano e magenta.",
    sublabel: "700 moedas",
    isPro: false,
    styleType: "standard",
    palette: {
      bg: "#08080f",
      surface: "#0e0e1c",
      primary: "#00f5cc",
      secondary: "#f000b8",
    },
  },
  {
    id: "paper",
    label: "Papel Antigo",
    description: "Pergaminho sépia, tipografia clássica e vintage.",
    sublabel: "400 moedas",
    isPro: false,
    styleType: "standard",
    palette: {
      bg: "#f0e6d3",
      surface: "#e8d9c0",
      primary: "#5c3d11",
      secondary: "#9e7b4a",
    },
  },
  // ── Temas do redesign (Claude Design, 07/2026) — 400 moedas cada ──────────
  {
    id: "cerejeira",
    label: "Cerejeira",
    description: "Papel rosado e um vermelho-cereja afetivo.",
    sublabel: "Gratuito",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#f8f0ee", surface: "#ffffff", primary: "#b23a5f", secondary: "#b07d2a" },
  },
  {
    id: "eucalipto",
    label: "Eucalipto",
    description: "Verde-sálvia calmo, leitura sem pressa.",
    sublabel: "400 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#eef2ec", surface: "#ffffff", primary: "#2f6b4f", secondary: "#977726" },
  },
  {
    id: "indigo",
    label: "Índigo",
    description: "Azul-índigo profundo sobre névoa fria.",
    sublabel: "400 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#eef0f5", surface: "#ffffff", primary: "#3a46b0", secondary: "#9a7a2e" },
  },
  {
    id: "cafe",
    label: "Café",
    description: "Espresso quente pra ler de madrugada.",
    sublabel: "400 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#17110c", surface: "#211911", primary: "#e3674a", secondary: "#e0b352" },
  },
  {
    id: "tinta",
    label: "Tinta",
    description: "Tinta sépia sobre papel, quase monocromático.",
    sublabel: "400 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#f3f1ec", surface: "#ffffff", primary: "#3b2d20", secondary: "#9a7d3a" },
  },
  // ── Leva 08/2026 (referências do fundador) ────────────────────────────────
  {
    id: "marinho",
    label: "Marinho",
    description: "Creme envelhecido e tinta naval — clássico de bolso.",
    sublabel: "400 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#fdf3c9", surface: "#fffcf0", primary: "#0f3c65", secondary: "#b07d1f" },
  },
  {
    id: "ameixa",
    label: "Ameixa",
    description: "Lavanda clara com um roxo de ameixa madura.",
    sublabel: "400 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#f4eff5", surface: "#ffffff", primary: "#763a69", secondary: "#b26599" },
  },
  {
    id: "veludo",
    label: "Veludo",
    description: "Oxblood profundo e rosa chiclete — romance sem pudor.",
    sublabel: "700 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#1a0505", surface: "#260909", primary: "#f698bd", secondary: "#e0a95c" },
  },
  {
    id: "absinto",
    label: "Absinto",
    description: "Púrpura de licor com um verde-limão elétrico.",
    sublabel: "700 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#1a0410", surface: "#280719", primary: "#cad183", secondary: "#dfae5c" },
  },
  // ── Temporada Eclipse (2026) — os dois primeiros SAZONAIS ─────────────────
  // ⚠️ Nada aqui sabe que eles são sazonais, e é de propósito. Este arquivo é
  // de onde `resolveEquippedThemeId` lê, e o resolvedor responde "que tema
  // aparece", nunca "que tema está à venda". A janela vive em
  // `config/themeSeasons.ts` e só é consultada pela vitrine e pelo servidor.
  // Trazê-la para cá faria todo comprador perder o tema quando a temporada
  // fechasse. Ver docs/systems/seasonal-themes.md §3.
  //
  // Paletas MEDIDAS contra `palettesLookSame` (papel <= 12 e tinta <= 25
  // colidem): as primeiras candidatas colidiam com `diamante` (papel 5,5) e
  // `grifo` (papel 7,8) — os dois `earned` de 31/08 já ocupavam "noite azul
  // fria" e "dia warm white". Estas ficam a 20,1 e 17,7 do vizinho mais
  // próximo, ou seja com margem, não no fio da navalha.
  {
    id: "lua",
    label: "Lua",
    description: "Noite índigo profunda, prata e um brilho lunar por cima.",
    sublabel: "1500 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#100a2e", surface: "#1b1443", primary: "#a8b6f0", secondary: "#d9d4f2" },
  },
  {
    id: "sol",
    label: "Sol",
    description: "Parede quente de fim de tarde, âmbar e luz batendo de cima.",
    sublabel: "1500 moedas",
    isPro: false,
    styleType: "standard",
    palette: { bg: "#fff0dd", surface: "#fffaf3", primary: "#ba5009", secondary: "#9a4a12" },
  },
  // Evento Halloween (07/10/2026) — grátis só de 15/10 a 02/11. Ver o
  // comentário da entrada no `themeRegistry`.
  {
    id: "halloween",
    label: "Halloween",
    description: "Noite violeta, abóbora e uma visita de oito patas.",
    sublabel: "Gratuito",
    isPro: false,
    event: "halloween",
    styleType: "standard",
    palette: { bg: "#160b21", surface: "#241332", primary: "#ff7f1f", secondary: "#c79bff" },
  },
  {
    id: "borgonha",
    label: "Borgonha",
    description: "Preto absoluto, vinho e off-white. Nada sobra.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "standard",
    palette: { bg: "#0a0908", surface: "#15100f", primary: "#d4566b", secondary: "#c2a15a" },
  },
  {
    id: "aurora",
    label: "Aurora",
    description: "Violeta, índigo e um teal frio — a luz do fim da tarde.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "standard",
    palette: { bg: "#2b193d", surface: "#2c365e", primary: "#6cbdb8", secondary: "#c5979d" },
  },
  {
    id: "alcova",
    label: "Alcova",
    description: "Rosa empoeirado sob luz de abajur.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "standard",
    palette: { bg: "#1e1519", surface: "#291d23", primary: "#e59aa4", secondary: "#dcb079" },
  },
  {
    id: "sakura",
    label: "Sakura",
    description: "Rosa de primavera com pétalas caindo no seu perfil.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "standard",
    palette: { bg: "#ffe4ee", surface: "#fff7fa", primary: "#c62d63", secondary: "#8d5e23" },
  },
  {
    id: "vitral",
    label: "Vitral",
    description: "Luz de catedral em Cinzel, com poeira colorida no perfil.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "standard",
    palette: { bg: "#e8e4f5", surface: "#f7f5fd", primary: "#5b3fa8", secondary: "#286d90" },
  },
  // Leva PRO F3 (25/09): paletas medidas contra `palettesLookSame` com margem.
  {
    id: "romantasy",
    label: "Romantasy",
    description: "Noite de ameixa e rosa antigo, com pétalas e ouro caindo no seu perfil.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "standard",
    palette: { bg: "#220d2b", surface: "#2f173a", primary: "#f08fb8", secondary: "#e2b86b" },
  },
  {
    id: "academia",
    label: "Dark Academia",
    description: "Verde de biblioteca e papel envelhecido, com luz de janela e poeira no seu perfil.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "standard",
    palette: { bg: "#0e2016", surface: "#172a1f", primary: "#9ec48a", secondary: "#d0a55c" },
  },
  {
    id: "latao",
    label: "Latão",
    description: "Preto de cofre e latão polido — a placa gravada virou tema.",
    sublabel: "Livra Founder",
    isPro: false,
    isFounder: true,
    styleType: "standard",
    palette: { bg: "#0b0a08", surface: "#16130e", primary: "#c9a355", secondary: "#e8cf9a" },
  },
  {
    id: "obsidian",
    label: "Obsidian Pulse",
    description: "Preto absoluto, violeta e ciano — profundidade neon-glass.",
    sublabel: "Livra PRO",
    isPro: true,
    isPremium: true,
    styleType: "neon-glass",
    palette: {
      bg: "#050505",
      surface: "#0a0a0a",
      primary: "#a78bfa",
      secondary: "#22d3ee",
    },
  },
  // ── Tier `earned` (31/08) — os primeiros temas que não se compram ─────────
  // Paletas escolhidas contra o dedup do Share Studio (`palettesLookSame`),
  // que é o critério objetivo que o produto já usa: tema que colapsa noutro
  // chip não acrescenta cor nenhuma ao catálogo. As três passam com folga —
  // o vizinho mais próximo de cada uma está a 13,3 · 14,6 · 15,3 de papel,
  // contra o teto de 12.
  {
    id: "diamante",
    label: "Diamante",
    description: "Gelo sobre grafite — o azul frio do topo da liga.",
    sublabel: "Conquistado",
    isPro: false,
    requiresBadge: "imperador_diamante",
    styleType: "standard",
    palette: { bg: "#0c141d", surface: "#131e2a", primary: "#5cc8e8", secondary: "#a8d8ea" },
  },
  {
    id: "brasa",
    label: "Brasa",
    description: "Carvão e laranja vivo — a chama que não apagou.",
    sublabel: "Conquistado",
    isPro: false,
    requiresBadge: "ofensiva_100",
    styleType: "standard",
    palette: { bg: "#1f1512", surface: "#2b1d17", primary: "#f2622a", secondary: "#f2a63a" },
  },
  {
    id: "grifo",
    label: "Grifo",
    description: "Papel de caderno e marca-texto amarelo.",
    sublabel: "Conquistado",
    isPro: false,
    requiresBadge: "critico_publico",
    styleType: "standard",
    palette: { bg: "#fffdf2", surface: "#ffffff", primary: "#8a6d0e", secondary: "#b8890c" },
  },
];

/** Resolve metadados do tema pelo id (Settings, ThemeContext). */
export function getThemeById(themeId: string): ThemeOption | undefined {
  return THEMES.find((t) => t.id === themeId);
}

/** Estilo estrutural do tema (vidro PRO vs sólido). */
export function getThemeStyleType(themeId: string): ThemeStyleType {
  return getThemeById(themeId)?.styleType ?? "standard";
}

/** Tema bloqueado para quem não é assinante (isPro e/ou isPremium). */
export function themeRequiresSubscription(
  theme: Pick<ThemeOption, "isPro" | "isPremium">,
): boolean {
  return theme.isPro === true || theme.isPremium === true;
}

/** Tema bloqueado para quem não é Founder (`subscriptions.isLifetime`). */
export function themeRequiresFounder(theme: Pick<ThemeOption, "isFounder">): boolean {
  return theme.isFounder === true;
}

/** Tema que só existe para quem tem a badge declarada em `requiresBadge`. */
export function themeRequiresBadge(theme: Pick<ThemeOption, "requiresBadge">): boolean {
  return typeof theme.requiresBadge === "string" && theme.requiresBadge.length > 0;
}

/**
 * IDs de temas disponíveis gratuitamente.
 *
 * ⚠️ Derivada por EXCLUSÃO: todo gate novo precisa entrar neste filtro, senão
 * o tema gated vira gratuito para a base inteira sem ninguém notar (é o que
 * aconteceria com o Latão se `themeRequiresFounder` ficasse de fora).
 */
export const FREE_THEME_IDS = THEMES.filter(
  (t) => !themeRequiresSubscription(t) && !themeRequiresFounder(t),
).map((t) => t.id);

/**
 * Tema conquistado → a badge que o concede.
 *
 * Mapa e não lista: quem gateia é `resolveEquippedThemeId`, que precisa saber
 * QUAL badge exigir. Derivado do catálogo de propósito — lista paralela é como
 * `themeId` e `themePreference` divergiram (BUG-80).
 */
export const THEME_BADGE_REQUIREMENT: Record<string, string> = Object.fromEntries(
  THEMES.filter(themeRequiresBadge).map((t) => [t.id, t.requiresBadge as string]),
);

/** IDs de temas conquistados por badge — nem comprados, nem de assinatura. */
export const EARNED_THEME_IDS: readonly string[] = Object.keys(THEME_BADGE_REQUIREMENT);

/** Tema que só existe durante um evento (`themeEvents.ts`). */
export function themeIsEvent(theme: Pick<ThemeOption, "event">): boolean {
  return typeof theme.event === "string" && theme.event.length > 0;
}

/**
 * Tema de evento → o evento dele. É o que `resolveEquippedThemeId` consulta.
 * Derivado do catálogo, como o mapa de badges — lista paralela é BUG-80.
 */
export const THEME_EVENT_OF: Record<string, string> = Object.fromEntries(
  THEMES.filter(themeIsEvent).map((t) => [t.id, t.event as string]),
);

/**
 * O tema aparece na GRADE do passo 1 do onboarding?
 *
 * Vive aqui e não no componente por duas razões. A primeira é o lint: exportar
 * helper de arquivo de componente quebra o fast refresh, e o `eslint.config.js`
 * proíbe nominalmente somar arquivo novo à allowlist. A segunda é melhor —
 * esta regra e a de baixo são as duas metades da mesma pergunta ("o que uma
 * conta nova pode fazer com este tema?"), e lado a lado a divergência entre
 * elas fica visível: a tela esconde o que a pessoa nunca poderia ter, o
 * servidor recusa o que ela não pode gravar. Tema de MOEDA é o caso em que as
 * duas discordam de propósito — some da grade, mas segue aceito na escrita.
 *
 * ⚠️ Founder e conquistado saem pelo MESMO motivo, e ele se repete a cada tier
 * novo: nenhum dos dois exige assinatura nem tem preço, então passariam pelos
 * filtros da grade e nasceriam como escolha gratuita. No conquistado é pior
 * que no Founder — ali não há sequer um "ainda não" a comunicar, porque conta
 * nova não tem badge nenhuma, por definição.
 */
export function isThemeHiddenAtOnboarding(
  theme: Pick<ThemeOption, "id" | "isFounder" | "requiresBadge" | "event">,
): boolean {
  // Tema de EVENTO também sai: escolhido no passo 1, ele valeria dez dias e
  // depois devolveria a conta nova a um tema que ela nunca escolheu.
  return (
    theme.id === "custom" ||
    themeRequiresFounder(theme) ||
    themeRequiresBadge(theme) ||
    themeIsEvent(theme)
  );
}

/**
 * O tema pode ser gravado como preferência no FIM do onboarding?
 *
 * Dono único da regra, consumido por `completeUserOnboarding`. A lista larga
 * sozinha não serve: tema CONQUISTADO vive dentro dela de propósito (é o que
 * impede o guard de PRO de matá-lo para quem é grátis e ganhou a badge), e uma
 * conta que está terminando o onboarding não tem badge nenhuma — aceitar o id
 * gravaria uma preferência que o resolvedor devolve como "default" no primeiro
 * render, que é o downgrade silencioso do BUG-95.
 *
 * ⚠️ Tema de MOEDA continua aceito aqui, como sempre foi. Não é descuido: é o
 * comportamento vigente, e mudá-lo é outra decisão. Quem o barra na prática é
 * o guard de coin-gate do resolvedor, e a tela do passo 1 já não o oferece.
 */
export function isThemeAcceptedAtOnboarding(themeId: string): boolean {
  return (
    FREE_THEME_IDS.includes(themeId) &&
    !EARNED_THEME_IDS.includes(themeId) &&
    !(themeId in THEME_EVENT_OF)
  );
}

/** IDs de temas que requerem compra com moedas (sem assinatura PRO, mas não gratuitos) */
export const COIN_GATED_THEME_IDS = [
  "cyberpunk",
  "paper",
  "eucalipto",
  "indigo",
  "cafe",
  "tinta",
  "marinho",
  "ameixa",
  "veludo",
  "absinto",
  // Temporada Eclipse. Sazonal É coin-gated: o que a temporada muda é estar à
  // VENDA, e quem compra entra em `unlockedThemes` como qualquer outro. Tirar
  // os dois desta lista faria o guard de coin-gate parar de proteger, e o tema
  // de 1.500 moedas ficaria de graça para a base inteira.
  "lua",
  "sol",
] as const;

// ---------------------------------------------------------------------------
// Paletas de exportação (ExportCardTemplate, WrappedCard)
// Cores em hex puro — nunca oklch — para compatibilidade com html-to-image.
// ---------------------------------------------------------------------------

export interface ExportPalette {
  bg: string;
  surface: string;
  surfaceAlt: string;
  border: string;
  primary: string;
  secondary: string;
  muted: string;
  faint: string;
}

export const EXPORT_PALETTES: Record<string, ExportPalette> = {
  sakura:   { bg: "#ffe4ee", surface: "#fff7fa", surfaceAlt: "#ffd3e2", border: "#f4b8ce", primary: "#3f1024", secondary: "#c62d63", muted: "#8a4a63", faint: "#ffd3e2" },
  vitral:   { bg: "#e8e4f5", surface: "#f7f5fd", surfaceAlt: "#dcd6ef", border: "#c3b9e0", primary: "#1d1636", secondary: "#5b3fa8", muted: "#5d5480", faint: "#dcd6ef" },
  romantasy: { bg: "#220d2b", surface: "#2f173a", surfaceAlt: "#3e204b", border: "#4a2a58", primary: "#f7e8f1", secondary: "#f08fb8", muted: "#c9aecb", faint: "#3e204b" },
  academia:  { bg: "#0e2016", surface: "#172a1f", surfaceAlt: "#20352a", border: "#2f4436", primary: "#ece6d4", secondary: "#9ec48a", muted: "#b1ad96", faint: "#20352a" },
  default:  { bg: "#f4f1ec", surface: "#ffffff", surfaceAlt: "#efe9dd", border: "#e6dfd2", primary: "#2a2521", secondary: "#bc4b2f", muted: "#6f675c", faint: "#efe9dd" },
  cozy:     { bg: "#101010", surface: "#1a1a1a", surfaceAlt: "#252525", border: "#2a2a2a", primary: "#f1dfc8", secondary: "#dfb682", muted: "#8a7055", faint: "#3a2e20" },
  cerejeira: { bg: "#f8f0ee", surface: "#ffffff", surfaceAlt: "#f4eaec", border: "#ecdde0", primary: "#2e2126", secondary: "#b23a5f", muted: "#7a626a", faint: "#f4eaec" },
  eucalipto: { bg: "#eef2ec", surface: "#ffffff", surfaceAlt: "#e7efe6", border: "#dce6dc", primary: "#1f2b23", secondary: "#2f6b4f", muted: "#5f7065", faint: "#e7efe6" },
  indigo:   { bg: "#eef0f5", surface: "#ffffff", surfaceAlt: "#e8eaf1", border: "#dde0ea", primary: "#1c2232", secondary: "#3a46b0", muted: "#5f667c", faint: "#e8eaf1" },
  cafe:     { bg: "#17110c", surface: "#211911", surfaceAlt: "#2a2014", border: "#342718", primary: "#f1e7da", secondary: "#e3674a", muted: "#ab9685", faint: "#342718" },
  tinta:    { bg: "#f3f1ec", surface: "#ffffff", surfaceAlt: "#efeae0", border: "#e4e0d6", primary: "#232019", secondary: "#3b2d20", muted: "#6f6a5f", faint: "#efeae0" },
  midnight: { bg: "#0a090e", surface: "#14121f", surfaceAlt: "#1e1a2e", border: "#231a2e", primary: "#f2eaf0", secondary: "#bf3f5a", muted: "#7a4a65", faint: "#2d1a2a" },
  matcha:   { bg: "#F2EFE9", surface: "#E5E2D9", surfaceAlt: "#D8D5CB", border: "#CDD0C5", primary: "#35422f", secondary: "#6A7B62", muted: "#7a8a72", faint: "#CDD0C5" },
  obsidian:  { bg: "#050505", surface: "#0a0a0a", surfaceAlt: "#121212", border: "rgba(255,255,255,0.1)", primary: "#a78bfa", secondary: "#22d3ee", muted: "#71717a", faint: "#27272a" },
  noir:      { bg: "#111111", surface: "#1c1c1c", surfaceAlt: "#252525", border: "#2a2a2a", primary: "#e2e2e2", secondary: "#888888", muted: "#a3a3a3", faint: "#525252" },
  cyberpunk: { bg: "#08080f", surface: "#0e0e1c", surfaceAlt: "#14142a", border: "rgba(0,245,204,0.18)", primary: "#00f5cc", secondary: "#f000b8", muted: "#4a8a80", faint: "#1a1a30" },
  paper:     { bg: "#f0e6d3", surface: "#e8d9c0", surfaceAlt: "#ddd0b5", border: "#c8b49a", primary: "#5c3d11", secondary: "#9e7b4a", muted: "#8a6b45", faint: "#c8b49a" },
  marinho:   { bg: "#fdf3c9", surface: "#fffcf0", surfaceAlt: "#f7e9b4", border: "#eddda3", primary: "#12283f", secondary: "#0f3c65", muted: "#5a6a7c", faint: "#f7e9b4" },
  ameixa:    { bg: "#f4eff5", surface: "#ffffff", surfaceAlt: "#ece4ee", border: "#e2d7e5", primary: "#2a1626", secondary: "#763a69", muted: "#6f5a6b", faint: "#ece4ee" },
  veludo:    { bg: "#1a0505", surface: "#260909", surfaceAlt: "#360d0d", border: "#471414", primary: "#fbe9ef", secondary: "#f698bd", muted: "#c79aa6", faint: "#360d0d" },
  absinto:   { bg: "#1a0410", surface: "#280719", surfaceAlt: "#380a22", border: "#4b0f2d", primary: "#f3eee0", secondary: "#cad183", muted: "#bb9cab", faint: "#380a22" },
  lua:       { bg: "#100a2e", surface: "#1b1443", surfaceAlt: "#251c58", border: "#332876", primary: "#ece9fc", secondary: "#a8b6f0", muted: "#a9a1d4", faint: "#251c58" },
  sol:       { bg: "#fff0dd", surface: "#fffaf3", surfaceAlt: "#ffe3bf", border: "#f2d5ab", primary: "#2e1e07", secondary: "#ba5009", muted: "#6d5329", faint: "#ffe3bf" },
  borgonha:  { bg: "#0a0908", surface: "#15100f", surfaceAlt: "#49111c", border: "#3a1119", primary: "#f2f4f3", secondary: "#d4566b", muted: "#a79b9d", faint: "#49111c" },
  aurora:    { bg: "#2b193d", surface: "#2c365e", surfaceAlt: "#484d6d", border: "#3d4468", primary: "#eae6f2", secondary: "#6cbdb8", muted: "#a9a3c0", faint: "#484d6d" },
  alcova:    { bg: "#1e1519", surface: "#291d23", surfaceAlt: "#372630", border: "#44303a", primary: "#f6e7ec", secondary: "#e59aa4", muted: "#c2a3ae", faint: "#372630" },
  diamante:  { bg: "#0c141d", surface: "#131e2a", surfaceAlt: "#1c2a38", border: "#28394a", primary: "#e4eef6", secondary: "#5cc8e8", muted: "#93a7b8", faint: "#1c2a38" },
  brasa:     { bg: "#1f1512", surface: "#2b1d17", surfaceAlt: "#3a2820", border: "#4a3327", primary: "#ffe2bd", secondary: "#f2622a", muted: "#c0a086", faint: "#3a2820" },
  grifo:     { bg: "#fffdf2", surface: "#ffffff", surfaceAlt: "#fdf3c0", border: "#e9e0bc", primary: "#26241c", secondary: "#8a6d0e", muted: "#6b6553", faint: "#fdf3c0" },
  latao:     { bg: "#0b0a08", surface: "#16130e", surfaceAlt: "#241d12", border: "#3a2f1d", primary: "#f4efe4", secondary: "#c9a355", muted: "#aa9d86", faint: "#241d12" },
  halloween: { bg: "#160b21", surface: "#241332", surfaceAlt: "#301b42", border: "#452a5a", primary: "#fbe4c6", secondary: "#ff7f1f", muted: "#bfa9c4", faint: "#301b42" },
};

/** Retorna a paleta de exportação para o tema dado (fallback: default) */
export function getExportPalette(themeId: string | undefined): ExportPalette {
  return EXPORT_PALETTES[themeId ?? "default"] ?? EXPORT_PALETTES["default"];
}
